/**
 * FikoYT CAM — Yönetici (SIFIRDAN). Manufacturing Program ağacı, operasyon CRUD,
 * takım kütüphanesi, toolpath hesap + 3D render, replay (tool animasyonu), post-processor köprüsü.
 */
import * as THREE from 'three';
import { IPartOperasyon, ICamOperasyon, ICamToolpath, ICamHareket, ITakim, OperasyonTip, OPERASYON_AD, TakimTip, AXIAL_TIPLER, ICamMakro, IMakroAdim, MakroAdimTip, ITakimYonu, TakimYonKaynak } from './fiko-cam.types';
import { FikoCamToolpath, camExactOffsetBagla } from './fiko-cam-toolpath';
import { fikoOCCT } from '../fiko-modelleme/fiko-occt-kernel';
import { FikoCamPost } from './fiko-cam-postprocessor';
import { FikoCamGorsel } from './fiko-cam-gorsel';
import { FikoCamToolAxisDialog } from './fiko-cam-tool-axis-dialog';
import { FikoCamPartOpDialog } from './fiko-cam-part-op-dialog';
import { FikoTakimYoluv1 } from '../FikoThreeCam/FikoTakimYoluv1';
import { FikoVektorTakimYol3Dv1 } from '../FikoThreeCam/FikoVektorTakimYol3dv1';
import { edgeGruplariUret } from '../fiko-modelleme/fiko-govde-metadata';
import { MALZEME_KESIM } from '../Fiko-2d-Cam/strategies/feeds-speeds';

const HAREKET_TUR: Record<string, string> = { rapid: 'BOSTA', retract: 'UZAKLASMA', plunge: 'YAKLASMA', approach: 'YAKLASMA', feed: 'ISLEME' };

export interface CamDeps {
  sahne: THREE.Scene;
  kameraAl: () => THREE.Camera | null | undefined;
  canvasAl?: () => HTMLCanvasElement | null | undefined;
  ciz: () => void;
  bildir?: (m: string) => void;
  govdeAl?: () => THREE.Mesh | null | undefined;   // işlenecek part body (geometri/seçim için)
  // Ürün ağacı entegrasyonu (CATIA Manufacturing Program → aktiviteler)
  aktiviteEkle?: (op: ICamOperasyon) => any;       // ağaca operasyon node'u ekle, node döndür
  aktiviteSil?: (op: ICamOperasyon) => void;       // ağaçtan node'u kaldır
  takimAgacEkle?: (tk: ITakim) => any;             // ağaca takım kaynağı node'u ekle (CATIA Resource List)
  takimAgacSil?: (tk: ITakim) => void;             // ağaçtan takım node'u kaldır
  agacGuncelle?: () => void;                        // ürün ağacı UI yenile
  durumYaz?: (json: any) => void;                   // CAM durumunu kalıcı yere yaz (kaydet/oku için)
  gecmisDegisti?: (geri: boolean, ileri: boolean) => void;   // undo/redo buton durumu
  secimDurum?: (d: { mod: 'nokta' | 'kontur' | 'ada' | 'toolaxis' | 'kenar' | 'duzlem' | 'noktaref'; opAd: string; sayi: number; ek?: string } | null) => void;   // 3D seçim HUD (CATIA seçim feedback)
  yuzZAl?: (faceId: number) => number | null;   // associatif düzlem: faceId → o yüzün LOCAL Z'si (occt_faceGroups center)
  brepKontur?: (faceId: number) => { segmentler: { tip: string; noktalar: { x: number; y: number; z: number }[]; arc?: any }[] } | null;   // CATIA: yüzün GERÇEK B-Rep analitik konturu (varsa) — mesh yerine kullanılır
  delikAdaylari?: () => { x: number; y: number; z: number; cap: number; derinlik: number }[];   // CATIA otomatik delik tanıma: B-Rep silindirik yüzlerden delik adayları (dünya koordinatı)
  yuzNormalAl?: (faceId: number) => { x: number; y: number; z: number } | null;   // CATIA associative tool axis: faceId → güncel B-Rep yüz normali (dünya)
  knowledgeAl?: () => any;   // FikoKnowledgeYonetici (fiko-modelleme/knowledge) — parametrik CAM: op alanları f(x) formül alabilir
}

let _idc = 0; const yeniId = (p: string) => p + '_' + (Date.now().toString(36)) + (_idc++);

export class FikoCamYonetici {
  partOp: IPartOperasyon;
  takimlar: ITakim[] = [];
  private toolpaths = new Map<string, ICamToolpath>();
  private gorsel: FikoCamGorsel | null = null;
  private makroOp: ICamOperasyon | null = null;   // makro görseli açık olan operasyon
  private yolGrubu: THREE.Group | null = null;
  private toolMesh: THREE.Mesh | null = null;
  private replayRaf = 0;

  constructor(private d: CamDeps) {
    this.takimlar = this.takimlarYukle() || this.varsayilanTakimlar();
    this.partOp = {
      id: yeniId('po'), ad: 'Part Operation.1', makineTip: '3axis',
      eksenOrigin: { x: 0, y: 0, z: 0 }, guvenlikZ: 50, operasyonlar: [],
    };
    // Direktif B: toolpath offset'leri OCCT B-Rep exact (kernel hazırsa; değilse JS fallback otomatik)
    camExactOffsetBagla((poly, dIce) => { try { return fikoOCCT.poligonOffset2D(poly, dIce); } catch { return null; } });
  }

  // ───────── Takım kütüphanesi kalıcılığı (localStorage — oturumlar arası korunur) ─────────
  private static readonly TAKIM_LS = 'fikoCamTakimlar';
  private takimlarYukle(): ITakim[] | null {
    try { const s = localStorage.getItem(FikoCamYonetici.TAKIM_LS); if (!s) return null; const a = JSON.parse(s); return Array.isArray(a) && a.length ? a : null; } catch { return null; }
  }
  private takimlarSakla(): void {
    try { localStorage.setItem(FikoCamYonetici.TAKIM_LS, JSON.stringify(this.takimlar)); } catch { }
  }

  // ───────── Takım kütüphanesi ─────────
  private varsayilanTakimlar(): ITakim[] {
    const t = (ad: string, tip: TakimTip, cap: number, ek: Partial<ITakim> = {}): ITakim => ({ id: yeniId('tk'), ad, tip, cap, boy: cap * 3, agizSayisi: 2, numara: 0, ...ek });
    const arr = [
      t('Tarama Ø50', 'faceMill', 50, { agizSayisi: 4 }),
      t('Parmak Freze Ø10', 'endMill', 10, { agizSayisi: 4 }),
      t('Parmak Freze Ø6', 'endMill', 6, { agizSayisi: 3 }),
      t('Küresel Ø6', 'ballMill', 6, { kose: 3 }),
      t('Spot Matkap Ø8', 'spotDrill', 8, { ucAci: 90 }),
      t('Matkap Ø5', 'drill', 5, { ucAci: 118 }),
      t('Matkap Ø8.5', 'drill', 8.5, { ucAci: 118 }),
      t('Kılavuz M10', 'tap', 10, { hatve: 1.5 }),
      t('Rayba Ø10', 'reamer', 10),
      t('Bara Ø20', 'boringBar', 20),
      t('Havşa 90°', 'countersink', 12, { ucAci: 90 }),
    ];
    arr.forEach((x, i) => x.numara = i + 1);
    return arr;
  }
  takimEkle(tip: TakimTip, cap: number, ad?: string): ITakim {
    const tk: ITakim = { id: yeniId('tk'), ad: ad || ('Takım Ø' + cap), tip, cap, boy: cap * 3, toplamBoy: cap * 6, agizSayisi: 2, numara: this.takimlar.length + 1 };
    this.takimlar.push(tk);
    this.d.takimAgacEkle?.(tk); this.operasyonMenuleriniYenile(); this.d.agacGuncelle?.(); this.durumKaydet(); this.takimlarSakla();
    return tk;
  }
  /** Takım kütüphanesi değişince operasyon node'larının sağ-menü "Tool Change" listesini güncelle. */
  private operasyonMenuleriniYenile(): void { for (const o of this.partOp.operasyonlar) o.node?.sagMenuKur?.(); }
  /** Takım parametresi/ölçüsü değişti (dialog) — kullanan operasyonları yeniden hesapla + kalıcılık. */
  takimDegisti(tk: ITakim): void {
    const kullanan = this.partOp.operasyonlar.filter(o => o.takimId === tk.id);
    for (const o of kullanan) { o.hesaplandi = false; this.hesapla(o); }
    this.d.agacGuncelle?.(); this.durumKaydet(); this.takimlarSakla();
  }
  /** Takımı sil — kullanan operasyon yoksa kaldırır, varsa uyarır. */
  takimSil(tk: ITakim): boolean {
    const kullanan = this.partOp.operasyonlar.filter(o => o.takimId === tk.id);
    if (kullanan.length) { this.d.bildir?.('Takım ' + kullanan.length + ' operasyonda kullanılıyor — silinemez'); return false; }
    this.takimlar = this.takimlar.filter(t => t !== tk);
    this.d.takimAgacSil?.(tk); this.operasyonMenuleriniYenile(); this.d.agacGuncelle?.(); this.durumKaydet(); this.takimlarSakla();
    return true;
  }

  // ───────── Operasyon CRUD ─────────
  operasyonEkle(tip: OperasyonTip): ICamOperasyon {
    const axial = AXIAL_TIPLER.includes(tip as any);
    const ust = (this.partOp.stok ? this.partOp.stok.z + this.partOp.stok.yuk : 0);
    const op: ICamOperasyon = {
      id: yeniId('op'), ad: OPERASYON_AD[tip] + '.' + (this.partOp.operasyonlar.length + 1), tip,
      takimId: this.uygunTakim(tip)?.id,
      geometri: { ustZ: ust, altZ: ust - 10, derinlik: 10, konturlar: [], noktalar: [] },
      strateji: axial
        ? { dongu: tip === 'deepHole' ? 'peck' : tip === 'breakChips' ? 'breakChip' : 'standart', peckDerinlik: 3, geriCekme: 1, dwellMod: tip === 'boringStop' ? 'devir' : 'kapali', dwellDeger: 10 }
        : { toolPathStyle: tip === 'pocketing' ? 'helicalIn' : 'backForth', yon: 'climb', koseTip: 'optimized', makineToleransi: 0.01, radialMod: 'pctTool', yanalHatveYuzde: 40, yanalHatve: 0, axialMod: 'maxDepth', eksenelHatve: 2, seviyeSayisi: 1, finisMod: 'yok', finisPayi: 0, tabanPayi: 0.3, rasterAci: 0, overhang: 50, endOfPath: 'outside' },
      hizlar: { devir: axial ? 1200 : 3000, ilerleme: axial ? 120 : 600, dalmaIlerleme: axial ? 80 : 200 },
      makrolar: this.varsayilanMakrolar(tip, axial, ust),
    };
    // raster default hatve = takım çapı * 0.6
    if (!axial) { const tk = this.takimlar.find(t => t.id === op.takimId); op.strateji.yanalHatve = (tk?.cap || 6) * 0.6; }
    this.partOp.operasyonlar.push(op);
    op.node = this.d.aktiviteEkle?.(op);   // CATIA: Manufacturing Program altına aktivite node'u
    this.d.agacGuncelle?.();
    this.durumKaydet();
    return op;
  }

  /** CATIA otomatik delik tanıma: B-Rep silindirik yüzlerden delik merkezlerini bulup çapa göre gruplu drilling operasyonları üretir. Bulunan delik sayısını döndürür. */
  delikleriOtomatikBul(): number {
    const adaylar = this.d.delikAdaylari?.() || [];
    if (!adaylar.length) { this.d.bildir?.('Delik bulunamadı — gövdede dikey silindirik yüz yok (B-Rep gövde gerekli)'); return 0; }
    const gruplar = new Map<string, { x: number; y: number; z: number; cap: number; derinlik: number }[]>();
    for (const a of adaylar) { const k = (Math.round(a.cap * 10) / 10).toFixed(1); let g = gruplar.get(k); if (!g) { g = []; gruplar.set(k, g); } g.push(a); }
    let opSay = 0;
    for (const [k, grup] of gruplar) {
      const cap = parseFloat(k);
      const tk = this.capaYakinMatkap(cap);
      const op = this.operasyonEkle('drilling');
      op.takimId = tk.id;
      op.ad = 'Delik Ø' + k + ' ×' + grup.length;
      const ustZ = Math.max(...grup.map(g => g.z));
      const maxDer = Math.max(...grup.map(g => g.derinlik || 10));
      op.geometri.ustZ = ustZ; op.geometri.derinlik = maxDer; op.geometri.altZ = ustZ - maxDer;
      op.geometri.noktalar = grup.map(g => ({ x: g.x, y: g.y, z: g.z }));
      this.hizHesapla(op); this.hesapla(op);
      opSay++;
    }
    this.d.agacGuncelle?.(); this.durumKaydet();
    this.d.bildir?.(adaylar.length + ' delik bulundu → ' + opSay + ' matkap operasyonu oluşturuldu');
    return adaylar.length;
  }
  /** Çapa en yakın matkabı bulur; yeterince yakını yoksa o çapta yeni matkap ekler. */
  private capaYakinMatkap(cap: number): ITakim {
    const drills = this.takimlar.filter(t => t.tip === 'drill');
    let en = drills.length ? drills.reduce((a, b) => Math.abs((b.cap || 0) - cap) < Math.abs((a.cap || 0) - cap) ? b : a) : undefined;
    if (!en || Math.abs((en.cap || 0) - cap) > 0.3) en = this.takimEkle('drill', Math.round(cap * 10) / 10, 'Matkap Ø' + (Math.round(cap * 10) / 10));
    return en;
  }

  // ───────── CATIA Tool Axis (takım yönü — 3+2 / 5-axis) ─────────
  /** Operasyona takım yönü ata + yeniden hesapla. null → part Z varsayılan. */
  takimYonuAyarla(op: ICamOperasyon, ty: ITakimYonu | null): void {
    if (ty && ty.yon) {
      const l = Math.hypot(ty.yon.x, ty.yon.y, ty.yon.z) || 1;
      ty.yon = { x: ty.yon.x / l, y: ty.yon.y / l, z: ty.yon.z / l };
      op.takimYonu = ty;
    } else { delete op.takimYonu; }
    op.hesaplandi = false; this.hesapla(op); this.d.agacGuncelle?.(); this.durumKaydet();
    this.d.bildir?.('Takım yönü: ' + this.takimYonuEtiket(op));
  }
  /** Sabit düzlem ekseninden takım yönü (xy→+Z, xz→+Y, yz→+X). */
  takimYonuDuzlem(op: ICamOperasyon, duzlem: 'xy' | 'xz' | 'yz'): void {
    const yon = duzlem === 'yz' ? { x: 1, y: 0, z: 0 } : duzlem === 'xz' ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
    this.takimYonuAyarla(op, { yon, kaynak: 'plane' });
  }
  /** Seçilen B-Rep yüz normalinden takım yönü (component yüzey seçimi sağlar). */
  takimYonuYuzey(op: ICamOperasyon, normal: { x: number; y: number; z: number }, nokta?: { x: number; y: number; z: number }, faceId?: number): void {
    this.takimYonuAyarla(op, { yon: normal, kaynak: 'yuzey', yuzId: faceId, origin: nokta });
  }
  /** CATIA "Tool Axis Definition" ayrı dialog'unu aç (IJK düzenle + parametrik yüze bağla). */
  toolAxisDialogAc(op: ICamOperasyon): void {
    const df = (this as any)._df; if (!df?.ac) { this.d.bildir?.('Dialog servisi yok'); return; }
    df.ac({
      bilesen: FikoCamToolAxisDialog,
      id: 'fiko-cam-toolaxis-' + op.id,
      ayarlar: { baslik: 'Takım Ekseni Tanımı', simgeResim: './simgeler/3d.webp' },
      parametreler: { element: op, yonetici: this },
    });
  }
  /** CATIA "Part Operation" kurulum penceresi (makine/work offset/malzeme/güvenlik Z/stok/eksen orijini). */
  partOpDialogAc(): void {
    const df = (this as any)._df; if (!df?.ac) { this.d.bildir?.('Dialog servisi yok'); return; }
    df.ac({
      bilesen: FikoCamPartOpDialog,
      id: 'fiko-cam-partop',
      ayarlar: { baslik: 'Parça Operasyonu', simgeResim: './simgeler/3d.webp' },
      parametreler: { element: this.partOp, yonetici: this },
    });
  }

  /** UI: takım yönü etiketi. */
  takimYonuEtiket(op: ICamOperasyon): string {
    const ty = op.takimYonu; if (!ty || !ty.yon) return 'Z (varsayılan)';
    const y = ty.yon, f = (n: number) => +n.toFixed(2);
    if (Math.abs(y.z) > 0.999) return 'Z'; if (Math.abs(y.y) > 0.999) return 'Y'; if (Math.abs(y.x) > 0.999) return 'X';
    return (ty.kaynak === 'yuzey' ? 'Yüzey ' : '') + '(' + f(y.x) + ',' + f(y.y) + ',' + f(y.z) + ')';
  }

  /** CATIA varsayılan makro seti — Approach + Retract + Clearance (her biri ayrı tanımlı). */
  private varsayilanMakrolar(tip: OperasyonTip, axial: boolean, ust: number): ICamMakro[] {
    const cap = (this.takimlar.find(t => t.tip === 'endMill')?.cap || 6);
    return [
      { tip: 'clearance', aktif: true, guvenlikZ: this.partOp.guvenlikZ, yaklasmaZ: ust + 2 },
      // pocketing → helis dalış; contour/profil/eğri → teğet yay (rampa cebe dalış içindir, kontura uymaz); diğer → rampa
      { tip: 'approach', aktif: true, hareketTip: axial ? 'on' : (tip === 'pocketing' ? 'helis' : (tip === 'contouring' || tip === 'curveFollow' ? 'yay' : 'rampa')), aci: 3, yaricap: cap * 0.4, guvenlikZ: this.partOp.guvenlikZ, yaklasmaZ: ust + 2 },
      { tip: 'retract', aktif: true, hareketTip: 'dikine', mesafe: 2 },
    ];
  }
  /** Operasyona yeni makro ekler (CATIA: birden fazla ayrı makro). */
  makroEkle(op: ICamOperasyon, tip: ICamMakro['tip']): ICamMakro {
    const m: ICamMakro = { tip, aktif: true, hareketTip: 'dikine', aci: 3, yaricap: 3, mesafe: 2, guvenlikZ: this.partOp.guvenlikZ };
    (op.makrolar = op.makrolar || []).push(m); this.durumKaydet(); return m;
  }
  makroSil(op: ICamOperasyon, m: ICamMakro): void { op.makrolar = (op.makrolar || []).filter(x => x !== m); this.durumKaydet(); }
  /** CATIA Build by user: özel makro adımı ekle (varsayılan parametrelerle). */
  makroAdimEkle(m: ICamMakro, tip: MakroAdimTip): void {
    const v: Record<string, IMakroAdim> = {
      eksenel: { tip: 'eksenel', mesafe: 3 }, teget: { tip: 'teget', mesafe: 3 },
      rampa: { tip: 'rampa', mesafe: 5, aci: 5 }, helis: { tip: 'helis', mesafe: 3, aci: 4, yaricap: 3 },
      duzleme: { tip: 'duzleme', hedefZ: 0 },
    };
    (m.adimlar = m.adimlar || []).push({ ...v[tip] }); this.durumKaydet();
  }
  makroAdimSil(m: ICamMakro, i: number): void { m.adimlar?.splice(i, 1); this.durumKaydet(); }
  makroAdimTasi(m: ICamMakro, i: number, yon: number): void { const a = m.adimlar; if (!a) return; const j = i + yon; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; this.durumKaydet(); }
  /** CATIA Feeds&Speeds: kesme hızı (Vc) ve diş başına ilerlemeden (fz) devir + ilerleme hesapla. */
  hizHesapla(op: ICamOperasyon): void {
    const tk = this.takimlar.find(t => t.id === op.takimId); if (!tk) return;
    const D = tk.cap || 6;
    // Varsayılan Vc (m/min) ve fz (mm/diş) — takım tipine göre (alüminyum/çelik ortalama)
    const varsayilan: Record<string, [number, number]> = {
      faceMill: [180, 0.15], endMill: [120, 0.05], ballMill: [100, 0.04], bullMill: [120, 0.05],
      drill: [80, 0.12], spotDrill: [60, 0.08], tap: [12, 0], reamer: [40, 0.15], boringBar: [120, 0.1],
      countersink: [60, 0.08], counterbore: [80, 0.1], tslotMill: [80, 0.04], threadMill: [100, 0.04],
    };
    let [vcVar, fzVar] = varsayilan[tk.tip] || [100, 0.05];
    // Malzeme seçiliyse (Part Operation Setup) Vc/fz malzeme tablosundan — freze tam, delme ailesi Vc %50 / fz %120 (pratik kural)
    const mlz = MALZEME_KESIM[this.partOp.malzeme || ''];
    if (mlz) {
      const delme = ['drill', 'spotDrill', 'centerDrill', 'reamer', 'boringBar', 'countersink', 'counterbore', 'multiDrill'].includes(tk.tip);
      vcVar = delme ? mlz.vc * 0.5 : mlz.vc;
      fzVar = delme ? mlz.fz * 1.2 : mlz.fz;
    }
    const vc = op.hizlar.kesimHiz || vcVar;
    const fz = op.hizlar.disBasinaIlerleme || fzVar;
    const z = tk.agizSayisi || (AXIAL_TIPLER.includes(op.tip as any) ? 1 : 2);
    const rpm = Math.round((vc * 1000) / (Math.PI * D));
    op.hizlar.kesimHiz = vc; op.hizlar.disBasinaIlerleme = fz;
    op.hizlar.devir = rpm;
    // Kılavuz (tap): ilerleme = devir × hatve (senkron); diğerleri: devir × fz × ağız
    if (tk.tip === 'tap' || op.tip === 'tapping' || op.tip === 'reverseTap') op.hizlar.ilerleme = Math.round(rpm * (tk.hatve || 1.5));
    else op.hizlar.ilerleme = Math.round(rpm * fz * z);
    op.hizlar.dalmaIlerleme = Math.round(op.hizlar.ilerleme * 0.4);
    op.hesaplandi = false;
  }
  private uygunTakim(tip: OperasyonTip): ITakim | undefined {
    const map: Partial<Record<OperasyonTip, TakimTip>> = {
      facing: 'faceMill', pocketing: 'endMill', contouring: 'endMill', curveFollow: 'endMill', circularMill: 'endMill', pointToPoint: 'endMill',
      spotDrill: 'spotDrill', drilling: 'drill', deepHole: 'drill', breakChips: 'drill', tapping: 'tap', reverseTap: 'tap',
      boring: 'boringBar', boringChamfer: 'boringBar', boringStop: 'boringBar', reaming: 'reamer',
      counterSink: 'countersink', counterBore: 'counterbore', tslotting: 'tslotMill', threadMilling: 'threadMill',
    };
    const istenen = map[tip]; return this.takimlar.find(t => t.tip === istenen) || this.takimlar[1];
  }
  operasyonSil(op: ICamOperasyon): void { this.partOp.operasyonlar = this.partOp.operasyonlar.filter(o => o !== op); this.toolpaths.delete(op.id); this.camKnowledgeTemizle(op); this.d.aktiviteSil?.(op); this.d.agacGuncelle?.(); this.tumYollariCiz(); this.durumKaydet(); }

  /** CATIA Copy Transformation — operasyonu öteleme/dönüş/ayna ile çoğaltır (machining pattern). Yeni operasyonlar döndürür. */
  kopyalaDonustur(op: ICamOperasyon, p: { tip: 'translate' | 'rotate' | 'symmetry'; adet?: number; dx?: number; dy?: number; aci?: number; mx?: number; my?: number; aynaEksen?: 'x' | 'y' }): ICamOperasyon[] {
    const adet = p.tip === 'symmetry' ? 1 : Math.max(1, p.adet || 1);
    const yeni: ICamOperasyon[] = [];
    for (let k = 1; k <= adet; k++) {
      const kopya: ICamOperasyon = JSON.parse(JSON.stringify({ ...op, node: undefined }));
      kopya.id = yeniId('op'); kopya.ad = op.ad + ' (Kopya ' + k + ')'; kopya.hesaplandi = false;
      const don = (pt: { x: number; y: number }) => {
        if (p.tip === 'translate') return { x: pt.x + (p.dx || 0) * k, y: pt.y + (p.dy || 0) * k };
        if (p.tip === 'rotate') { const a = (p.aci || 0) * k * Math.PI / 180, cx = p.mx || 0, cy = p.my || 0, dx = pt.x - cx, dy = pt.y - cy; return { x: cx + dx * Math.cos(a) - dy * Math.sin(a), y: cy + dx * Math.sin(a) + dy * Math.cos(a) }; }
        return p.aynaEksen === 'x' ? { x: pt.x, y: 2 * (p.my || 0) - pt.y } : { x: 2 * (p.mx || 0) - pt.x, y: pt.y };   // symmetry
      };
      const g = kopya.geometri;
      g.konturlar = (g.konturlar || []).map(c => c.map(don));
      g.adalar = (g.adalar || []).map(c => c.map(don));
      g.noktalar = (g.noktalar || []).map(pt => ({ ...don(pt), z: pt.z }));
      this.partOp.operasyonlar.push(kopya);
      kopya.node = this.d.aktiviteEkle?.(kopya);
      yeni.push(kopya);
    }
    this.d.agacGuncelle?.(); this.hesapla(); this.durumKaydet();
    this.d.bildir?.('Copy Transformation: ' + yeni.length + ' kopya oluşturuldu');
    return yeni;
  }

  // ───────── Kalıcılık (kaydet/oku) + Undo/Redo ─────────
  /** Tüm CAM durumunu JSON'a çevir (node referansları hariç). */
  serialize(): any {
    return JSON.parse(JSON.stringify({ v: 1, partOp: this.partOp, takimlar: this.takimlar }, (k, val) => (k === 'node' || k === 'yolNode') ? undefined : val));
  }
  /** JSON'dan CAM durumunu geri yükle — aktivite ağaç node'larını yeniden kurar. */
  deserialize(o: any): void {
    if (!o?.partOp) return;
    for (const op of this.partOp.operasyonlar) this.d.aktiviteSil?.(op);   // eski node'ları temizle
    this.toolpaths.clear();
    if (o.takimlar?.length) this.takimlar = o.takimlar;
    this.partOp = o.partOp;
    for (const op of this.partOp.operasyonlar) op.node = this.d.aktiviteEkle?.(op);
    this.hesapla();
    this.d.agacGuncelle?.();
  }
  /** Değişiklik sonrası: kalıcı yere yaz + geçmişe ekle. */
  durumKaydet(): void {
    if (this._undoKilit) return;
    const j = this.serialize();
    this.d.durumYaz?.(j);
    this.gecmisKaydet(j);
  }
  private _gecmis: string[] = [];
  private _gIdx = -1;
  private _undoKilit = false;
  private readonly G_MAX = 60;
  gecmisKaydet(j?: any): void {
    const snap = JSON.stringify(j || this.serialize());
    if (this._gIdx >= 0 && this._gecmis[this._gIdx] === snap) return;
    this._gecmis = this._gecmis.slice(0, this._gIdx + 1);
    this._gecmis.push(snap);
    if (this._gecmis.length > this.G_MAX) this._gecmis.shift();
    this._gIdx = this._gecmis.length - 1;
    this.gecmisBildir();
  }
  gecmisSifirla(): void { this._gecmis = []; this._gIdx = -1; this.gecmisKaydet(); }
  get geriAlinabilir(): boolean { return this._gIdx > 0; }
  get ileriAlinabilir(): boolean { return this._gIdx >= 0 && this._gIdx < this._gecmis.length - 1; }
  geriAl(): void { if (!this.geriAlinabilir) return; this._gIdx--; this.gecmisUygula(); this.d.bildir?.('CAM: geri alındı'); }
  ileriAl(): void { if (!this.ileriAlinabilir) return; this._gIdx++; this.gecmisUygula(); this.d.bildir?.('CAM: ileri alındı'); }
  private gecmisUygula(): void {
    this._undoKilit = true;
    try { const o = JSON.parse(this._gecmis[this._gIdx]); this.deserialize(o); this.d.durumYaz?.(o); } catch (e) { console.warn('[CAM undo]', e); }
    this._undoKilit = false; this.gecmisBildir();
  }
  private gecmisBildir(): void { this.d.gecmisDegisti?.(this.geriAlinabilir, this.ileriAlinabilir); }

  // ───────── Parametrik CAM (Knowledge f(x) köprüsü — CATIA) ─────────
  /** Op alanı → knowledge intrinsic parametre yayın haritası. Formül bağlanabilir (örn `Cep.1\YanalHatve` = `Takım Çapı * 0.4` ya da tasarım parametresine bağ). */
  private static readonly CAM_ALANLAR: { yol: 'geometri' | 'strateji' | 'hizlar'; alan: string; etiket: string; tip: 'Uzunluk' | 'Tamsayi' | 'Reel' }[] = [
    { yol: 'geometri', alan: 'derinlik', etiket: 'Derinlik', tip: 'Uzunluk' },
    { yol: 'geometri', alan: 'ustZ', etiket: 'ÜstZ', tip: 'Uzunluk' },
    { yol: 'geometri', alan: 'altZ', etiket: 'AltZ', tip: 'Uzunluk' },
    { yol: 'strateji', alan: 'eksenelHatve', etiket: 'KesimDerinliği', tip: 'Uzunluk' },
    { yol: 'strateji', alan: 'yanalHatve', etiket: 'YanalHatve', tip: 'Uzunluk' },
    { yol: 'strateji', alan: 'finisPayi', etiket: 'FinişPayı', tip: 'Uzunluk' },
    { yol: 'hizlar', alan: 'devir', etiket: 'Devir', tip: 'Tamsayi' },
    { yol: 'hizlar', alan: 'ilerleme', etiket: 'İlerleme', tip: 'Tamsayi' },
  ];
  /** Parametrik senkron (hesapla başında): op alanları depoya intrinsic yayınlanır (dış değişiklik parametreye),
   * formüller çözülür, çözülen değerler op alanlarına write-through yazılır (_sonYazilan kalıbı — CAD ile aynı). */
  private camKnowledgeSenkron(liste: ICamOperasyon[]): void {
    const kn = this.d.knowledgeAl?.(); if (!kn?.depo) return;
    try {
      let yeniVar = false;
      for (const o of liste) {
        if (o.bastirilmis) continue;
        const featId = 'cam_' + o.id;
        const grup = kn.depo.featGrupAl(featId, '⚙ ' + o.ad);
        for (const t of FikoCamYonetici.CAM_ALANLAR) {
          const v = (o as any)[t.yol]?.[t.alan];
          if (typeof v !== 'number' || !Number.isFinite(v)) continue;   // tanımsız alan yayınlanmaz
          if (!kn.depo.getFeatAlan(featId, t.alan)) yeniVar = true;
          kn.depo.featAlanYayinla(featId, o.ad, t.alan, t.etiket, t.tip, v, grup.id);
        }
      }
      kn.cozumle();   // formülleri değerlendir (CAD+CAM birlikte, bağımlılık grafiğiyle)
      for (const o of liste) {
        if (o.bastirilmis) continue;
        const featId = 'cam_' + o.id;
        for (const t of FikoCamYonetici.CAM_ALANLAR) {
          const p = kn.depo.getFeatAlan(featId, t.alan); if (!p) continue;
          const v = Number(p.deger); if (!Number.isFinite(v)) continue;
          (o as any)[t.yol][t.alan] = v; p._sonYazilan = v;   // depo tek otorite → alana yaz
        }
      }
      if (yeniVar) { kn.agacDegisti?.(); }   // ürün ağacı Parametreler grubu yeni CAM klasörünü göstersin
      kn.yayinla?.();   // workbench/dialog görünümleri tazelensin
    } catch (e) { console.warn('[CAM knowledge]', e); }
  }
  /** Silinen operasyonun knowledge parametrelerini/grubunu kaldır. */
  private camKnowledgeTemizle(op: ICamOperasyon): void {
    const kn = this.d.knowledgeAl?.(); if (!kn?.depo) return;
    try { const featId = 'cam_' + op.id; kn.depo.featKaldir(featId); kn.depo.gruplar = kn.depo.gruplar.filter((g: any) => g.featId !== featId); kn.agacDegisti?.(); kn.yayinla?.(); } catch { }
  }

  // ───────── Hesapla + Render ─────────
  hesapla(op?: ICamOperasyon): void {
    const liste = op ? [op] : this.partOp.operasyonlar;
    this.camKnowledgeSenkron(liste);   // parametrik CAM: f(x) formülleri çöz → op alanlarına uygula
    this.partDuzlemGuncelle();         // associatif Part Op güvenlik düzlemi (yüz + offset)
    for (const o of liste) { if (o.bastirilmis) { this.toolpathNodeKur(o); continue; } this.toolAxisParametrikGuncelle(o); this.makroDuzlemGuncelle(o); this.geoDuzlemGuncelle(o); const tk = this.takimlar.find(t => t.id === o.takimId); this.toolpaths.set(o.id, FikoCamToolpath.hesapla(o, tk)); o.hesaplandi = true; o.bayat = false; o.node?.etiketGuncelle?.(); this.toolpathNodeKur(o); }
    this.uyarilariTopla();
    this.tumYollariCiz();
    if (op) this.durumKaydet();   // operasyon düzenlemesi (dialog) → kalıcılık + undo
  }

  // ───────── Güvenlik uyarıları (gouge + toolpath) → UI ─────────
  /** Son hesaplamadan toplanan güvenlik uyarıları (statik gouge analizi + toolpath takım/doğrulama uyarıları). */
  sonUyarilar: { opId: string; opAd: string; tip: string; mesaj: string }[] = [];
  private _sonUyariImza = '';
  private uyarilariTopla(): void {
    const u = this.gougeKontrol();
    for (const o of this.partOp.operasyonlar) { const tu = this.toolpaths.get(o.id)?.uyarilar; if (tu) for (const m of tu) u.push({ opId: o.id, opAd: o.ad, tip: 'takım', mesaj: m }); }
    this.sonUyarilar = u;
    const imza = u.map(x => x.opId + '·' + x.tip).join('|');
    if (u.length && imza !== this._sonUyariImza) this.d.bildir?.('⚠ CAM: ' + u.length + ' güvenlik uyarısı — ' + u[0].opAd + ': ' + u[0].mesaj + (u.length > 1 ? ' (detay: Gouge)' : ''));
    this._sonUyariImza = imza;
  }

  // ───────── CATIA Associativity (parametrik CAM — model değişimini algıla) ─────────
  /** Update modu: true = model değişince toolpath'ler OTOMATİK yeniden hesaplanır; false = ⟳ bayat işaretle, kullanıcı Hesapla/Güncelle der (CATIA Manual Update). */
  otoGuncelle = false;
  /** Model/parametre değişti (gövde rebuild) → hesaplanmış toolpath'ler bayatlar. Associatif referanslar (yüz normali/Z) bir sonraki hesapla'da yeniden çözülür. */
  modelDegisti(): void {
    const ops = this.partOp.operasyonlar.filter(o => !o.bastirilmis && o.hesaplandi);
    if (!ops.length) return;
    if (this.otoGuncelle) { this.hesapla(); this.d.bildir?.('CAM: model değişti → ' + ops.length + ' takım yolu otomatik güncellendi'); return; }
    let n = 0; for (const o of ops) if (!o.bayat) { o.bayat = true; o.node?.etiketGuncelle?.(); n++; }
    if (n) { this.d.agacGuncelle?.(); this.d.bildir?.('CAM: model değişti — ' + n + ' takım yolu güncel değil (⟳). Hesapla ile güncelleyin.'); }
  }
  /** Bayat (güncel olmayan) operasyon var mı — ribbon rozeti için. */
  get bayatVar(): boolean { return this.partOp.operasyonlar.some(o => o.bayat && !o.bastirilmis); }
  /** CATIA associative tool axis: yüze bağlı (kaynak='yuzey', yuzId) takım yönünü güncel B-Rep yüz normaliyle yeniler — parça değişince eksen takip eder. */
  private toolAxisParametrikGuncelle(op: ICamOperasyon): void {
    const ty = op.takimYonu;
    if (ty?.kaynak !== 'yuzey' || ty.yuzId == null || !this.d.yuzNormalAl) return;
    const n = this.d.yuzNormalAl(ty.yuzId);
    if (n) { const l = Math.hypot(n.x, n.y, n.z) || 1; ty.yon = { x: n.x / l, y: n.y / l, z: n.z / l }; }
  }
  /** CATIA associatif düzlem: referans yüzlü (faceId) güvenlik/yaklaşma/düzleme-kadar Z'lerini güncel yüz Z'siyle yeniler. */
  private makroDuzlemGuncelle(op: ICamOperasyon): void {
    if (!this.d.yuzZAl) return;
    for (const m of op.makrolar || []) {
      if (m.guvenlikZRef != null) { const z = this.d.yuzZAl(m.guvenlikZRef); if (z != null) m.guvenlikZ = z; }
      if (m.yaklasmaZRef != null) { const z = this.d.yuzZAl(m.yaklasmaZRef); if (z != null) m.yaklasmaZ = z; }
      for (const a of m.adimlar || []) if (a.tip === 'duzleme' && a.hedefZRef != null) { const z = this.d.yuzZAl(a.hedefZRef); if (z != null) a.hedefZ = z; }
    }
  }
  /** CATIA associatif geometri kotları: ÜstZ/AltZ/Derinlik 3D yüz referanslıysa güncel yüz Z'siyle yenilenir (parça değişince takip eder). */
  private geoDuzlemGuncelle(op: ICamOperasyon): void {
    if (!this.d.yuzZAl) return;
    const g = op.geometri;
    if (g.ustZRef != null) { const z = this.d.yuzZAl(g.ustZRef); if (z != null) g.ustZ = z; }
    if (g.altZRef != null) { const z = this.d.yuzZAl(g.altZRef); if (z != null) g.altZ = z; }
    if (g.derinlikRef != null) { const z = this.d.yuzZAl(g.derinlikRef); if (z != null) g.derinlik = Math.max(0.1, (g.ustZ ?? 0) - z); }
  }
  /** CATIA associatif Part Operation güvenlik düzlemi: referans yüz Z + offset. */
  private partDuzlemGuncelle(): void {
    if (this.partOp.guvenlikZRef == null || !this.d.yuzZAl) return;
    const z = this.d.yuzZAl(this.partOp.guvenlikZRef);
    if (z != null) this.partOp.guvenlikZ = z + (this.partOp.guvenlikZOfset ?? 10);
  }
  /** Part Operation güvenlik düzlemini 3D'den seç (yüze tıkla → yüz kotu + offset; associatif referans saklanır). */
  partGuvenlikSecBaslat(gc?: () => void): void {
    const sahte: any = { id: 'po_' + this.partOp.id, ad: this.partOp.ad || 'Parça Operasyonu', tip: 'facing', geometri: {}, strateji: {}, hizlar: {}, makrolar: [] };
    this.camDuzlemSecBaslat(sahte, (z, faceId) => {
      this.partOp.guvenlikZRef = faceId;
      this.partOp.guvenlikZ = z + (this.partOp.guvenlikZOfset ?? 10);
      this.durumKaydet(); this.d.bildir?.('Güvenlik düzlemi: yüz kotu ' + z.toFixed(1) + ' + ' + (this.partOp.guvenlikZOfset ?? 10) + 'mm offset = Z' + this.partOp.guvenlikZ.toFixed(1) + (faceId != null ? ' (yüze bağlı)' : ''));
      gc?.();
    }, gc, 'GÜVENLİK DÜZLEMİ (Part Operation)');
  }
  toolpathAl(opId: string): ICamToolpath | undefined { return this.toolpaths.get(opId); }

  // ───────── CATIA: Toolpath = operasyon altında kalıcı FikoTakimYoluv1 node (FikoSimilasyonV1 uyumlu) ─────────
  /** Simülasyon takım mesh'i (tip-farkında: ball/bull/flat). */
  private simTakimMesh(tk: any): any {
    const r = (tk?.cap || 6) / 2, boy = Math.max(24, (tk?.cap || 6) * 4);
    const mat = new THREE.MeshStandardMaterial({ color: 0xb0b8c0, metalness: 0.85, roughness: 0.25 });
    const g: any = new THREE.Group();
    if (tk?.tip === 'ballMill') { const k = new THREE.Mesh(new THREE.SphereGeometry(r, 24, 16), mat); k.position.z = r; const s = new THREE.Mesh(new THREE.CylinderGeometry(r, r, boy - r, 20), mat); s.rotateX(Math.PI / 2); s.position.z = r + (boy - r) / 2; g.add(k, s); g.takimTipi = 'BallNose'; g.radius = r; }
    else if (tk?.tip === 'bullMill') { const kose = Math.min(r, tk.kose || r * 0.3); const t = new THREE.Mesh(new THREE.TorusGeometry(Math.max(0.01, r - kose), kose, 12, 24), mat); t.position.z = kose; const s = new THREE.Mesh(new THREE.CylinderGeometry(r, r, boy - kose, 20), mat); s.rotateX(Math.PI / 2); s.position.z = kose + (boy - kose) / 2; g.add(t, s); g.takimTipi = 'BullNose'; g.radius = kose; }
    else { const s = new THREE.Mesh(new THREE.CylinderGeometry(r, r, boy, 20), mat); s.rotateX(Math.PI / 2); s.position.z = boy / 2; g.add(s); g.takimTipi = 'Flat'; g.radius = 0; }
    g.name = 'camTakim'; g.cap = tk?.cap || 6; g.takimCap = tk?.cap || 6; g.takimUzunluk = boy; return g;
  }
  /** ICamHareket[] → FikoVektorTakimYol3Dv1[] (FikoSimilasyonV1 noktaları). */
  private hareketNoktalari(op: ICamOperasyon, hareketler: ICamHareket[], tk: any): FikoVektorTakimYol3Dv1[] {
    const tm = this.simTakimMesh(tk); const yon = new THREE.Vector3(0, 0, 1); const out: FikoVektorTakimYol3Dv1[] = [];
    for (const m of hareketler) {
      const v: any = new FikoVektorTakimYol3Dv1(m.x, m.y, m.z);
      v.hiz = m.f || op.hizlar.ilerleme || 200; v.devir = op.hizlar.devir || 3000; v.guncelSure = 0;
      v.hareketTuru = HAREKET_TUR[m.tip] || 'ISLEME'; v.takimYonu = yon.clone(); v.takim = tm; v.takimCap = tk?.cap || 6; v.takimUzunluk = tm.takimUzunluk;
      out.push(v);
    }
    return out;
  }
  /** Operasyon node'u (FikoCamAktivite) altına toolpath node'unu kur/güncelle — CATIA: tool path operasyonun altında. */
  private toolpathNodeKur(op: ICamOperasyon): void {
    const node: any = op.node; if (!node) return;
    const eski = (node.children || []).filter((c: any) => c.type === 'FikoTakimYoluv1');
    for (const e of eski) node.remove(e);
    const tp = this.toolpaths.get(op.id); if (!tp?.hareketler?.length) { op.yolNode = undefined; return; }
    const tk = this.takimlar.find(t => t.id === op.takimId);
    const yol: any = new FikoTakimYoluv1(node.sahne, (op.ad || 'Op') + ' · Takım Yolu', this.hareketNoktalari(op, tp.hareketler, tk));
    (node.ekle ? node.ekle(yol) : node.add(yol));
    op.yolNode = yol;
  }
  /** Toolpath hesapla + çiz ama undo/kalıcılık YAZMA (sürükleme gibi sık çağrılar için). */
  private hesaplaSessiz(op: ICamOperasyon): void {
    const tk = this.takimlar.find(t => t.id === op.takimId);
    this.toolpaths.set(op.id, FikoCamToolpath.hesapla(op, tk)); op.hesaplandi = true; op.node?.etiketGuncelle?.();
    this.tumYollariCiz();
  }

  private grupAl(): THREE.Group { if (!this.yolGrubu) { this.yolGrubu = new THREE.Group(); this.yolGrubu.name = 'FikoCamYollar'; this.d.sahne.add(this.yolGrubu); } return this.yolGrubu; }
  private yollarGorunur = false;   // CATIA: toolpath sahnede sürekli görünmez; sadece simülasyonda (FikoSimilasyonV1)
  private tumYollariCiz(): void {
    const g = this.grupAl(); for (let i = g.children.length - 1; i >= 0; i--) { const o = g.children[i]; g.remove(o); (o as any).geometry?.dispose?.(); (o as any).material?.dispose?.(); }
    if (!this.yollarGorunur) { this.d.ciz(); return; }   // gizli → segment çizme (toolpath verisi FikoTakimYoluv1'de tutulur)
    const renk = { rapid: 0xff3355, feed: 0x22dd66, plunge: 0x3388ff, retract: 0xff8800, approach: 0x00d0ff };
    for (const op of this.partOp.operasyonlar) {
      if (op.bastirilmis) continue; const tp = this.toolpaths.get(op.id); if (!tp) continue;
      // segmentleri tipe göre renkli çiz
      const grupla: Record<string, number[]> = {};
      for (let i = 1; i < tp.hareketler.length; i++) {
        const a = tp.hareketler[i - 1], b = tp.hareketler[i]; const key = b.tip;
        (grupla[key] = grupla[key] || []).push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
      for (const [tip, pts] of Object.entries(grupla)) {
        if (!pts.length) continue; const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
        const dash = tip === 'rapid' || tip === 'retract';
        const mat = new THREE.LineDashedMaterial({ color: (renk as any)[tip] || 0xffffff, dashSize: dash ? 1.5 : 1e6, gapSize: dash ? 1 : 0, depthTest: false, transparent: true, opacity: dash ? 0.7 : 1 });
        const ls = new THREE.LineSegments(geo, mat); ls.computeLineDistances(); ls.renderOrder = 998; (ls as any).raycast = () => { };
        g.add(ls);
      }
    }
    this.d.ciz();
  }
  yollariGizle(gizle: boolean): void { this.yollarGorunur = !gizle; this.tumYollariCiz(); }   // toggle: toolpath önizlemesini göster/gizle

  // ───────── Replay (takım animasyonu) ─────────
  replay(opId?: string): void {
    this.replayDurdur();
    const ops = opId ? this.partOp.operasyonlar.filter(o => o.id === opId) : this.partOp.operasyonlar.filter(o => !o.bastirilmis);
    const tumH = ops.flatMap(o => this.toolpaths.get(o.id)?.hareketler || []); if (!tumH.length) return;
    if (!this.toolMesh) { const g = new THREE.CylinderGeometry(3, 3, 16, 16); g.rotateX(Math.PI / 2); g.translate(0, 0, 8); this.toolMesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: 0xffcc00, depthTest: false, transparent: true, opacity: 0.85 })); this.toolMesh.renderOrder = 999; (this.toolMesh as any).raycast = () => { }; this.d.sahne.add(this.toolMesh); }
    this.toolMesh.visible = true; let i = 0;
    const tik = () => { if (i >= tumH.length) { this.replayDurdur(); return; } const m = tumH[i]; this.toolMesh!.position.set(m.x, m.y, m.z); this.d.ciz(); i += Math.max(1, Math.floor(tumH.length / 600)); this.replayRaf = requestAnimationFrame(tik); };
    tik();
  }
  replayDurdur(): void { if (this.replayRaf) cancelAnimationFrame(this.replayRaf); this.replayRaf = 0; if (this.toolMesh) this.toolMesh.visible = false; this.d.ciz(); }

  /**
   * Statik gouge/çarpma analizi (material removal güvenlik kontrolü):
   *  - rapid hareket malzeme kotunun altında (çarpma riski)
   *  - işleme (feed/plunge) taban kotunun altına iniyor (gouge; breakthrough hariç)
   *  - dik plunge çok derin (rampa/helis önerisi)
   * Operasyon başına benzersiz uyarı döndürür.
   */
  gougeKontrol(): { opId: string; opAd: string; tip: string; mesaj: string }[] {
    const out: { opId: string; opAd: string; tip: string; mesaj: string }[] = [];
    for (const op of this.partOp.operasyonlar) {
      if (op.bastirilmis) continue;
      const tp = this.toolpaths.get(op.id); if (!tp?.hareketler.length) continue;
      const g = op.geometri;
      const ust = g.ustZ ?? 0, alt = g.altZ ?? (ust - (g.derinlik ?? 10));
      const tabanLimit = (g.tabanAcik ? alt - (g.breakthrough ?? 0) : alt) - 0.05;
      const gor = new Set<string>();
      const ekle = (tip: string, mesaj: string) => { if (gor.has(tip)) return; gor.add(tip); out.push({ opId: op.id, opAd: op.ad, tip, mesaj }); };
      let maxPlunge = 0;
      for (let i = 0; i < tp.hareketler.length; i++) {
        const m = tp.hareketler[i];
        if (m.tip === 'rapid' && m.z < ust - 0.5) ekle('çarpma', 'Hızlı (rapid) hareket malzeme kotunun altında — Z=' + m.z.toFixed(1));
        if ((m.tip === 'feed' || m.tip === 'plunge') && m.z < tabanLimit) ekle('gouge', 'İşleme taban kotunun altına iniyor — Z=' + m.z.toFixed(1) + ' < ' + alt.toFixed(1));
        if (m.tip === 'plunge' && i > 0) { const p = tp.hareketler[i - 1]; if (Math.abs(m.x - p.x) < 0.01 && Math.abs(m.y - p.y) < 0.01) maxPlunge = Math.max(maxPlunge, p.z - m.z); }
      }
      const r = (this.takimlar.find(t => t.id === op.takimId)?.cap || 6) / 2;
      if (op.tip === 'pocketing' && maxPlunge > r * 2 && !op.makrolar?.some(mk => mk.tip === 'approach' && mk.aktif && (mk.hareketTip === 'helis' || mk.hareketTip === 'rampa')))
        ekle('plunge', 'Derin dik dalış (' + maxPlunge.toFixed(1) + ' mm) — helis/rampa giriş makrosu önerilir');
    }
    return out;
  }

  // ───────── Post-processor ─────────
  gcodeUret(): string { this.hesapla(); return FikoCamPost.gcode(this.partOp, this.toolpaths, this.takimlar); }
  aptUret(): string { this.hesapla(); return FikoCamPost.apt(this.partOp, this.toolpaths, this.takimlar); }
  dokumanUret(): string { this.hesapla(); return FikoCamPost.dokuman(this.partOp, this.toolpaths, this.takimlar); }

  // ───────── Stok / Part Operation ─────────
  stokOtomatik(): void {
    const gm = this.d.govdeAl?.(); if (!gm) { this.partOp.stok = { en: 100, boy: 100, yuk: 40, x: -50, y: -50, z: 0 }; return; }
    gm.geometry.computeBoundingBox(); const b = gm.geometry.boundingBox!; const pad = 2;
    this.partOp.stok = { en: (b.max.x - b.min.x) + pad * 2, boy: (b.max.y - b.min.y) + pad * 2, yuk: (b.max.z - b.min.z), x: b.min.x - pad, y: b.min.y - pad, z: b.min.z };
    this.partOp.guvenlikZ = b.max.z + 30;
  }
  temizle(): void { this.camSecimBitir(); this.replayDurdur(); this.gorsel?.yokEt(); this.gorsel = null; this.makroOp = null; if (this.yolGrubu) { this.d.sahne.remove(this.yolGrubu); this.yolGrubu = null; } if (this.toolMesh) { this.d.sahne.remove(this.toolMesh); this.toolMesh = null; } this.toolpaths.clear(); }

  // ───────── 3D Görselleştirme (seçim vurgu + makro düzlemleri) ─────────
  private gorselAl(): FikoCamGorsel {
    if (!this.gorsel) this.gorsel = new FikoCamGorsel({
      sahne: this.d.sahne, ciz: this.d.ciz,
      kameraAl: () => this.d.kameraAl(), canvasAl: () => this.d.canvasAl?.(),
      stokAl: () => this.partOp.stok,
    });
    return this.gorsel;
  }
  /** Operasyonun makro düzlemlerini 3D'de göster/gizle (toggle) — sürükle ile guvenlikZ/yaklasmaZ canlı ayarlanır. */
  makroGorselGoster(op: ICamOperasyon | null): void {
    const g = this.gorselAl();
    if (!op || this.makroOp === op) { this.makroOp = null; g.makroTemizle(); return; }
    this.makroOp = op;
    // sürükleme sırasında onChange her frame çağrılır → sessiz hesapla (undo/serialize taşmasın); kalıcılık CAM kapanış/sonraki işlemde
    const goster = () => { this.hesaplaSessiz(op); const tp = this.toolpaths.get(op.id); g.makroGoster(op, tp?.hareketler, goster); };
    goster();
  }
  /** Operasyonun seçili geometrisini (kontur/nokta) 3D'de vurgula. */
  secimGorselle(op: ICamOperasyon): void {
    const k = [...(op.geometri.konturlar || []), ...(op.geometri.adalar || [])];
    this.gorselAl().secimVurgu(k, op.geometri.noktalar, op.geometri.ustZ ?? 0);
  }
  /** TÜM 3D görsel katmanı temizle (seçim vurgusu + hover + makro düzlemleri + anim RAF). CAM kapanış/workbench-değişim/operasyon-kapatmada çağrılır — highlight sahnede kalmasın. */
  gorselTemizle(): void { this.makroOp = null; this.gorsel?.secimTemizle(); this.gorsel?.makroTemizle(); }

  // ───────── 3D Geometri seçimi (CATIA Geometry tab) ─────────
  private secModu: 'nokta' | 'kontur' | 'ada' | 'toolaxis' | 'kenar' | 'duzlem' | 'noktaref' | null = null;
  private secOp: ICamOperasyon | null = null;
  private duzlemAyarla?: (z: number, faceId?: number) => void;   // 'duzlem' modu: seçilen yüz Z'sini hedefe yaz
  private duzlemEtiket?: string;   // 'duzlem'/'noktaref' modu HUD ek açıklaması (Üst Limit / Limit 1 …)
  private noktaRefAyarla?: (x: number, y: number) => void;   // 'noktaref' modu: tıklanan LOCAL (x,y) sınır noktası
  private secGeriCagri?: () => void;
  private secHandler: ((e: PointerEvent) => void) | null = null;
  private hoverHandler: ((e: PointerEvent) => void) | null = null;
  private sonHoverFid = -1;
  private secEdgeIdx = new Set<number>();          // kenar modu: seçili kenar grubu indeksleri
  private kenarCache: any[] = [];                    // dondurulmuş occt_edgeGroups (rebuild-safe)
  private kenarCacheNoktalar: THREE.Vector3[][] = []; // her grubun LOCAL noktaları (hover/pick hot-path: tek seferlik genişletme)
  private sonHoverKenar = -1;
  get camNoktaSecAktif(): boolean { return this.secModu === 'nokta'; }
  get camKonturSecAktif(): boolean { return this.secModu === 'kontur'; }
  get camAdaSecAktif(): boolean { return this.secModu === 'ada'; }
  get camToolAxisSecAktif(): boolean { return this.secModu === 'toolaxis'; }
  get camKenarSecAktif(): boolean { return this.secModu === 'kenar'; }
  get camDuzlemSecAktif(): boolean { return this.secModu === 'duzlem'; }
  /** CATIA: gövde kenarlarını (occt_edgeGroups / EdgesGeometry) zincir olarak seç → kontur toolpath. */
  camKenarSecBaslat(op: ICamOperasyon, gc?: () => void): void { this.secBaslat('kenar', op, gc); this.d.bildir?.('İşlenecek kenarlara tıkla → seçilenler zincirlenip kontur olur (bitir: tekrar Seç)'); }
  /** CATIA: 3D'de bir yüze tıkla → o yüzün Z DÜZLEMİ (local) hedefe yazılır + faceId referansı (associatif). Güvenlik/Yaklaşma/Düzleme-Kadar için. */
  camDuzlemSecBaslat(op: ICamOperasyon, ayarla: (z: number, faceId?: number) => void, gc?: () => void, etiket?: string): void {
    this.secBaslat('duzlem', op, gc); this.duzlemAyarla = ayarla; this.duzlemEtiket = etiket;
    this.secimDurumBildir(); this.d.bildir?.((etiket || 'Düzlem') + ' için 3D gövdede bir yüze/düzleme tıkla → o düzlem referans alınır');
  }
  /** CATIA Limit 1/2: profil sınırı — 3D'de bir noktaya tıkla → LOCAL (x,y) sınır noktası callback'e yazılır. */
  camNoktaRefSecBaslat(op: ICamOperasyon, ayarla: (x: number, y: number) => void, gc?: () => void, etiket?: string): void {
    this.secBaslat('noktaref', op, gc); this.noktaRefAyarla = ayarla; this.duzlemEtiket = etiket;
    this.secimDurumBildir(); this.d.bildir?.((etiket || 'Sınır') + ' için profil üzerinde/parçada bir noktaya tıkla');
  }
  camAdaSecBaslat(op: ICamOperasyon, gc?: () => void): void { this.secBaslat('ada', op, gc); this.d.bildir?.('Cep içindeki ada (island) yüzüne tıkla — etrafından dolaşılır'); }

  /** Stok üst yüzünün dış konturunu operasyona ata (facing/cep/profil için hızlı geometri). */
  camStokKonturAta(op: ICamOperasyon): void {
    const st = this.partOp.stok; if (!st) { this.stokOtomatik(); }
    const s = this.partOp.stok!; const k = [{ x: s.x, y: s.y }, { x: s.x + s.en, y: s.y }, { x: s.x + s.en, y: s.y + s.boy }, { x: s.x, y: s.y + s.boy }];
    op.geometri.konturlar = [k]; op.geometri.ustZ = s.z + s.yuk; op.geometri.altZ = (op.geometri.altZ ?? s.z); op.hesaplandi = false;
    this.d.bildir?.('Stok sınırı kontur olarak atandı'); this.hesapla(op);
  }
  /** CATIA Machining Axis System: part operasyon eksen sistemini ayarla (NC kod bu eksene göre üretilir). */
  partEksenAyarla(p: { origin?: { x: number; y: number; z: number }; x?: { x: number; y: number; z: number }; y?: { x: number; y: number; z: number }; z?: { x: number; y: number; z: number } }): void {
    if (p.origin) this.partOp.eksenOrigin = p.origin;
    if (p.x) this.partOp.eksenX = p.x;
    if (p.y) this.partOp.eksenY = p.y;
    if (p.z) this.partOp.eksenZ = p.z;
    this.d.agacGuncelle?.(); this.durumKaydet();
    this.d.bildir?.('Makine eksen sistemi güncellendi');
  }
  camNoktaSecBaslat(op: ICamOperasyon, gc?: () => void): void { this.secBaslat('nokta', op, gc); this.d.bildir?.('Gövdede delik merkezlerine tıkla (bitir: tekrar Seç)'); }
  camKonturSecBaslat(op: ICamOperasyon, gc?: () => void): void { this.secBaslat('kontur', op, gc); this.d.bildir?.('İşlenecek yüze tıkla → sınır kontur olarak alınır'); }
  /** CATIA Tool Axis: bir yüze tıkla → yüz normali takım ekseni olur (B-Rep analitik, yoksa mesh). */
  camTakimYonuYuzeySecBaslat(op: ICamOperasyon, gc?: () => void): void { this.secBaslat('toolaxis', op, gc); this.d.bildir?.('Takım yönü için bir yüze tıkla (yüz normali = takım ekseni)'); }
  private secBaslat(mod: 'nokta' | 'kontur' | 'ada' | 'toolaxis' | 'kenar' | 'duzlem' | 'noktaref', op: ICamOperasyon, gc?: () => void): void {
    if (this.secModu === mod && this.secOp === op) { this.camSecimBitir(); gc?.(); return; }   // toggle
    this.camSecimBitir(); this.secModu = mod; this.secOp = op; this.secGeriCagri = gc;
    if (mod === 'kenar') { this.secEdgeIdx.clear(); this.kenarCache = this.camKenarGruplari(this.d.govdeAl?.() as any).slice(); this.kenarCacheNoktalar = this.kenarCache.map(eg => this.edgeGrupNoktalari(eg)); }
    const cv = this.d.canvasAl?.(); if (!cv) return;
    this.secHandler = (e) => this.secTik(e);
    this.hoverHandler = (e) => this.hoverTik(e);
    cv.addEventListener('pointerdown', this.secHandler, true);
    cv.addEventListener('pointermove', this.hoverHandler, true);
    this.secimDurumBildir();
  }
  /** 3D seçim HUD'unu güncelle (mod + seçilen öğe sayısı). */
  private secimDurumBildir(): void {
    if (!this.secModu || !this.secOp) { this.d.secimDurum?.(null); return; }
    const g = this.secOp.geometri;
    const sayi = this.secModu === 'nokta' ? (g.noktalar?.length || 0) : this.secModu === 'ada' ? (g.adalar?.length || 0) : this.secModu === 'kenar' ? this.secEdgeIdx.size : (g.konturlar?.length || 0);
    this.d.secimDurum?.({ mod: this.secModu, opAd: this.secOp.ad, sayi, ek: (this.secModu === 'duzlem' || this.secModu === 'noktaref') ? this.duzlemEtiket : undefined });
  }
  camSecimBitir(): void {
    const cv = this.d.canvasAl?.();
    if (cv && this.secHandler) cv.removeEventListener('pointerdown', this.secHandler, true);
    if (cv && this.hoverHandler) cv.removeEventListener('pointermove', this.hoverHandler, true);
    this.gorsel?.hoverVurgu(undefined); this.sonHoverFid = -1; this.sonHoverKenar = -1; this.kenarCache = []; this.kenarCacheNoktalar = []; this.duzlemAyarla = undefined; this.noktaRefAyarla = undefined;
    const gc = this.secGeriCagri;
    this.secHandler = null; this.hoverHandler = null; this.secModu = null; this.secOp = null; this.secGeriCagri = undefined;
    this.d.secimDurum?.(null);
    gc?.();   // dialog şemasını güncelle (seçim çerçevesi kalksın)
  }
  /** Hover: imleç altındaki yüzün sınırını mavi önizle (kontur modu). */
  private hoverTik(e: PointerEvent): void {
    if (this.secModu === 'kenar') {
      const i = this.enYakinKenarGrup(e);
      if (i === this.sonHoverKenar) return;
      this.sonHoverKenar = i;
      const pts = i >= 0 ? this.kenarCacheNoktalar[i] : null;
      this.gorselAl().hoverVurgu(pts && pts.length >= 2 ? pts.map(p => ({ x: p.x, y: p.y })) : undefined, pts?.[0]?.z);
      return;
    }
    if (this.secModu !== 'kontur') return;
    const gm = this.d.govdeAl?.(); const cv = this.d.canvasAl?.(); const kam = this.d.kameraAl();
    if (!gm || !cv || !kam) return;
    const r = cv.getBoundingClientRect();
    const ndc = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    const rc = new THREE.Raycaster(); rc.setFromCamera(ndc, kam as any);
    const hit = rc.intersectObject(gm, true)[0] as any;
    const grup = gm.geometry.attributes['grup'] as THREE.BufferAttribute | undefined;
    if (!hit || !grup || hit.faceIndex == null) { if (this.sonHoverFid !== -1) { this.sonHoverFid = -1; this.gorsel?.hoverVurgu(undefined); } return; }
    const fid = grup.getZ(hit.faceIndex * 3);
    if (fid === this.sonHoverFid) return;   // aynı yüz → tekrar hesaplama
    this.sonHoverFid = fid;
    const kontur = this.yuzKonturAl(gm, fid);
    this.gorselAl().hoverVurgu(kontur.length >= 3 ? kontur : undefined, hit.point.z);
  }
  private secTik(e: PointerEvent): void {
    const gm = this.d.govdeAl?.(); const cv = this.d.canvasAl?.(); const kam = this.d.kameraAl();
    if (!gm || !cv || !kam || !this.secOp) return;
    if (this.secModu === 'kenar') {   // kenar zinciri seçimi (raycast yerine ekran-uzaklığı)
      const i = this.enYakinKenarGrup(e); if (i < 0) return;
      e.stopPropagation(); e.preventDefault();
      if (this.secEdgeIdx.has(i)) this.secEdgeIdx.delete(i); else this.secEdgeIdx.add(i);   // toggle
      const kontur = this.kenarlardanKontur();
      let zTop = 0, zSay = 0;
      for (const idx of this.secEdgeIdx) { const np = this.kenarCacheNoktalar[idx]; if (np) for (const p of np) { zTop += p.z; zSay++; } }
      this.secOp.geometri.konturlar = kontur.length >= 2 ? [kontur] : [];
      if (zSay) this.secOp.geometri.ustZ = zTop / zSay;
      this.secOp.hesaplandi = false; this.secGeriCagri?.(); this.hesapla(this.secOp); this.secimGorselle(this.secOp); this.secimDurumBildir();
      this.d.bildir?.(this.secEdgeIdx.size + ' kenar seçili → ' + kontur.length + ' nokta kontur');
      return;
    }
    const r = cv.getBoundingClientRect();
    const ndc = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    const rc = new THREE.Raycaster(); rc.setFromCamera(ndc, kam as any);
    const hit = rc.intersectObject(gm, true)[0] as any; if (!hit) return;
    e.stopPropagation(); e.preventDefault();
    if (this.secModu === 'noktaref') {   // yüze/parçaya tıkla → LOCAL (x,y) sınır noktası (Limit 1/2)
      const lp = gm.worldToLocal(hit.point.clone());
      this.noktaRefAyarla?.(Math.round(lp.x * 100) / 100, Math.round(lp.y * 100) / 100);
      this.noktaRefAyarla = undefined; this.camSecimBitir();
      return;
    }
    if (this.secModu === 'duzlem') {   // yüze tıkla → o yüzün Z DÜZLEMİ (local) + faceId referansı
      const lz = gm.worldToLocal(hit.point.clone()).z;
      const grup = gm.geometry.attributes['grup'] as THREE.BufferAttribute | undefined;
      const faceId = (grup && hit.faceIndex != null) ? grup.getZ(hit.faceIndex * 3) : undefined;
      this.duzlemAyarla?.(Math.round(lz * 1000) / 1000, faceId);
      this.duzlemAyarla = undefined; this.camSecimBitir();
      return;
    }
    if (this.secModu === 'toolaxis') {
      const grup = gm.geometry.attributes['grup'] as THREE.BufferAttribute | undefined;
      const nm = new THREE.Matrix3().getNormalMatrix(gm.matrixWorld);
      let n: THREE.Vector3 | null = null; let fidSec: number | undefined;
      if (grup && hit.faceIndex != null) {   // B-Rep analitik yüz normali (yüz-ortalama)
        const fid = grup.getZ(hit.faceIndex * 3);
        const fg = (gm as any).occt_faceGroups?.[fid];
        if (fg?.normal) { n = new THREE.Vector3(fg.normal.x, fg.normal.y, fg.normal.z).applyMatrix3(nm).normalize(); fidSec = fid; }
      }
      if (!n && hit.face) n = hit.face.normal.clone().applyMatrix3(nm).normalize();   // fallback: mesh üçgen normali
      if (n) { this.takimYonuYuzey(this.secOp, { x: n.x, y: n.y, z: n.z }, { x: hit.point.x, y: hit.point.y, z: hit.point.z }, fidSec); this.secGeriCagri?.(); }
      this.camSecimBitir();
      return;
    }
    if (this.secModu === 'nokta') {
      (this.secOp.geometri.noktalar = this.secOp.geometri.noktalar || []).push({ x: hit.point.x, y: hit.point.y, z: hit.point.z });
      this.secOp.geometri.ustZ = hit.point.z; this.secOp.hesaplandi = false; this.secGeriCagri?.(); this.hesapla(this.secOp); this.secimGorselle(this.secOp); this.secimDurumBildir();
    } else {
      // yüz grubu → 2D sınır kontur (kontur = ana sınır, ada = cep içi engel)
      const grup = gm.geometry.attributes['grup'] as THREE.BufferAttribute | undefined;
      if (grup && hit.faceIndex != null) {
        const fid = grup.getZ(hit.faceIndex * 3);
        const brepMi = !!this.d.brepKontur?.(fid);
        const kontur = this.yuzKonturAl(gm, fid);
        if (kontur.length >= 3) {
          if (this.secModu === 'ada') { (this.secOp.geometri.adalar = this.secOp.geometri.adalar || []).push(kontur); this.d.bildir?.('Ada eklendi (' + kontur.length + ' nokta) — etrafından dolaşılır'); }
          else { this.secOp.geometri.konturlar = [kontur]; this.secOp.geometri.ustZ = hit.point.z; this.d.bildir?.('Yüz sınırı alındı (' + kontur.length + ' nokta' + (brepMi ? ', B-Rep analitik' : ', mesh') + ')'); }
          this.secOp.hesaplandi = false; this.secGeriCagri?.(); this.hesapla(this.secOp); this.secimGorselle(this.secOp); this.secimDurumBildir();
        }
      }
    }
  }
  /** CATIA: B-Rep varsa yüzün GERÇEK analitik konturunu (çizgi+yay, yay'lar örneklenir) XY'ye indirir; yoksa mesh sınırına düşer. */
  private yuzKonturAl(gm: THREE.Mesh, fid: number): { x: number; y: number }[] {
    const brep = this.d.brepKontur?.(fid);
    if (brep?.segmentler?.length) {
      const pts: { x: number; y: number }[] = [];
      const ekle = (x: number, y: number) => { const l = pts[pts.length - 1]; if (!l || Math.hypot(l.x - x, l.y - y) > 1e-4) pts.push({ x, y }); };
      for (const s of brep.segmentler) {
        if (s.tip === 'arc' && s.arc && s.noktalar.length >= 2) {
          const a = s.noktalar[0], b = s.noktalar[s.noktalar.length - 1]; const { cx, cy, r, cw } = s.arc;
          let a0 = Math.atan2(a.y - cy, a.x - cx), a1 = Math.atan2(b.y - cy, b.x - cx);
          if (cw) { while (a1 > a0 + 1e-9) a1 -= 2 * Math.PI; } else { while (a1 < a0 - 1e-9) a1 += 2 * Math.PI; }
          const N = Math.max(2, Math.ceil(Math.abs(a1 - a0) / (Math.PI / 24)));   // ~7.5° adım (pürüzsüz yay)
          for (let i = 0; i <= N; i++) { const ang = a0 + (a1 - a0) * (i / N); ekle(cx + Math.cos(ang) * r, cy + Math.sin(ang) * r); }
        } else { for (const p of s.noktalar) ekle(p.x, p.y); }
      }
      if (pts.length > 2) { const f = pts[0], l = pts[pts.length - 1]; if (Math.hypot(f.x - l.x, f.y - l.y) < 1e-3) pts.pop(); }   // kapalı: mükerrer son nokta at
      if (pts.length >= 3) return pts;
    }
    return this.yuzKonturXY(gm, fid);   // B-Rep yok → mesh sınırı
  }
  /** Shoelace alan (kapalı poligon, işaretli mutlak değer dış sınır seçiminde). */
  private alanXY(p: { x: number; y: number }[]): number { let a = 0; for (let i = 0, j = p.length - 1; i < p.length; j = i++) a += (p[j].x + p[i].x) * (p[j].y - p[i].y); return a / 2; }
  /** Mesh yüz grubunun XY-düzlemine izdüşmüş dış sınır konturu (boundary edge zinciri). */
  private yuzKonturXY(mesh: THREE.Mesh, fid: number): { x: number; y: number }[] {
    const geo = mesh.geometry; const grup = geo.attributes['grup'] as THREE.BufferAttribute; const pos = geo.attributes['position'] as THREE.BufferAttribute;
    const idx = geo.index; const triSay = idx ? idx.count / 3 : pos.count / 3;
    const key = (i: number) => Math.round(pos.getX(i) * 100) + ',' + Math.round(pos.getY(i) * 100);
    const xyOf: Record<string, { x: number; y: number }> = {};
    const kenarSay = new Map<string, number>(); const kenarUç = new Map<string, [string, string]>();
    const ekle = (a: number, b: number) => { const ka = key(a), kb = key(b); xyOf[ka] = { x: pos.getX(a), y: pos.getY(a) }; xyOf[kb] = { x: pos.getX(b), y: pos.getY(b) }; const k = ka < kb ? ka + '|' + kb : kb + '|' + ka; kenarSay.set(k, (kenarSay.get(k) || 0) + 1); kenarUç.set(k, [ka, kb]); };
    for (let t = 0; t < triSay; t++) { const i0 = idx ? idx.getX(t * 3) : t * 3, i1 = idx ? idx.getX(t * 3 + 1) : t * 3 + 1, i2 = idx ? idx.getX(t * 3 + 2) : t * 3 + 2; if (grup.getZ(i0) !== fid) continue; ekle(i0, i1); ekle(i1, i2); ekle(i2, i0); }
    // boundary = bir kez geçen kenarlar → kenar listesi (çoklu döngü/dallanma güvenli)
    const komsu = new Map<string, string[]>();
    const bkenar: [string, string][] = [];
    for (const [k, n] of kenarSay) { if (n !== 1) continue; const [a, b] = kenarUç.get(k)!; bkenar.push([a, b]); (komsu.get(a) || komsu.set(a, []).get(a))!.push(b); (komsu.get(b) || komsu.set(b, []).get(b))!.push(a); }
    if (!bkenar.length) return [];
    // TÜM boundary döngülerini çıkar (kullanılmış kenarları işaretle) → en geniş ALANLI döngü = dış sınır (yarım kalmaz)
    const kenarGor = new Set<string>(); const ek = (a: string, b: string) => (a < b ? a + '|' + b : b + '|' + a);
    const dongular: { x: number; y: number }[][] = [];
    for (const [s0, s1] of bkenar) {
      if (kenarGor.has(ek(s0, s1))) continue;
      const dongu: { x: number; y: number }[] = [xyOf[s0]]; let prev = s0, cur: string | undefined = s1; kenarGor.add(ek(s0, s1));
      let guard = 0;
      while (cur && cur !== s0 && guard++ < 20000) {
        dongu.push(xyOf[cur]);
        const nx = (komsu.get(cur) || []).filter(x => x !== prev && !kenarGor.has(ek(cur!, x)));
        if (!nx.length) break;
        kenarGor.add(ek(cur, nx[0])); prev = cur; cur = nx[0];
      }
      if (dongu.length >= 3) dongular.push(dongu);
    }
    if (!dongular.length) return [];
    let enGenis = dongular[0], enAlan = Math.abs(this.alanXY(enGenis));
    for (const d of dongular) { const a = Math.abs(this.alanXY(d)); if (a > enAlan) { enAlan = a; enGenis = d; } }
    return enGenis;
  }

  // ───────── CAM kenar (edge) grubu seçimi — occt_edgeGroups varsa onu, yoksa EdgesGeometry'den üret ─────────
  /** Gövde mesh'inin kenar gruplarını döndür (STEP B-Rep: occt_edgeGroups; mesh/WRL: EdgesGeometry → tek seferlik üretim, cache). */
  private camKenarGruplari(gm: THREE.Mesh | null | undefined): any[] {
    if (!gm) return [];
    const mevcut = (gm as any).occt_edgeGroups as any[];
    if (mevcut?.length) return mevcut;
    try {
      const eg = new THREE.EdgesGeometry(gm.geometry as any, 28);   // 28° feature-edge eşiği
      const { gruplar } = edgeGruplariUret(eg as any);
      eg.dispose();
      (gm as any).occt_edgeGroups = gruplar;   // sonraki seçimler için cache
      return gruplar;
    } catch (e) { return []; }
  }
  /** occt edge grubunu LOCAL geometri 3D noktalarına açar (B-Rep pts / CIRCLE / LINE).
   *  NOT: stok/yüz konturu/toolpath hep LOCAL geometri çerçevesinde — matrixWorld UYGULANMAZ (tutarlılık). Ekran seçimi için enYakinKenarGrup matrisi ayrıca uygular. */
  private edgeGrupNoktalari(eg: any): THREE.Vector3[] {
    const out: THREE.Vector3[] = [];
    const ekle = (x: number, y: number, z: number) => { out.push(new THREE.Vector3(x, y, z)); };
    if (eg.pts && eg.pts.length >= 2) { for (const p of eg.pts) ekle(p.x, p.y, p.z); return out; }
    if (eg.curveType === 'CIRCLE' && eg.center && eg.axis) {
      const c = new THREE.Vector3(eg.center.x, eg.center.y, eg.center.z);
      const ax = new THREE.Vector3(eg.axis.x, eg.axis.y, eg.axis.z).normalize();
      const u = new THREE.Vector3(1, 0, 0); if (Math.abs(ax.dot(u)) > 0.9) u.set(0, 1, 0);
      const e1 = new THREE.Vector3().crossVectors(ax, u).normalize();
      const e2 = new THREE.Vector3().crossVectors(ax, e1).normalize();
      const rad = eg.radius || 1;
      for (let i = 0; i <= 48; i++) { const a = (i / 48) * Math.PI * 2; const q = c.clone().addScaledVector(e1, Math.cos(a) * rad).addScaledVector(e2, Math.sin(a) * rad); ekle(q.x, q.y, q.z); }
    } else if (eg.start && eg.end) {
      const a = new THREE.Vector3(eg.start.x, eg.start.y, eg.start.z), b = new THREE.Vector3(eg.end.x, eg.end.y, eg.end.z);
      for (let i = 0; i <= 8; i++) { const q = a.clone().lerp(b, i / 8); ekle(q.x, q.y, q.z); }
    }
    return out;
  }
  /** İmlece en yakın kenar grubunu ekran-uzaklığı ile bul (22px). */
  private enYakinKenarGrup(e: PointerEvent | MouseEvent, thr = 22): number {
    const cv = this.d.canvasAl?.(); const kam = this.d.kameraAl(); if (!cv || !kam || !this.kenarCache.length) return -1;
    const gm = this.d.govdeAl?.(); const mw = gm?.matrixWorld;   // LOCAL nokta → WORLD (kamera projeksiyonu için)
    const r = cv.getBoundingClientRect(); const mx = (e as any).clientX - r.left, my = (e as any).clientY - r.top;
    let enYakin = -1, enMesafe = thr; const tmp = new THREE.Vector3();
    for (let i = 0; i < this.kenarCacheNoktalar.length; i++) {
      for (const p of this.kenarCacheNoktalar[i]) {
        tmp.copy(p); if (mw) tmp.applyMatrix4(mw); tmp.project(kam as any); if (tmp.z > 1) continue;
        const sx = (tmp.x * 0.5 + 0.5) * r.width, sy = (-tmp.y * 0.5 + 0.5) * r.height;
        const d = Math.hypot(sx - mx, sy - my); if (d < enMesafe) { enMesafe = d; enYakin = i; }
      }
    }
    return enYakin;
  }
  /** Seçili kenar gruplarını uç-uca zincirleyip tek (açık/kapalı) XY kontur üretir. */
  private kenarlardanKontur(): { x: number; y: number }[] {
    const parcalar: { x: number; y: number }[][] = [];
    for (const idx of this.secEdgeIdx) {
      const np = this.kenarCacheNoktalar[idx]; if (!np) continue;
      const pts = np.map(p => ({ x: p.x, y: p.y }));
      if (pts.length >= 2) parcalar.push(pts);
    }
    if (!parcalar.length) return [];
    if (parcalar.length === 1) return parcalar[0];
    const d2 = (a: any, b: any) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
    const tol2 = 25;   // (5mm)² uç birleşme toleransı (mesh/yay örnekleme toleranslı)
    const sonuc = parcalar.shift()!.slice();
    let guard = 0;
    while (parcalar.length && guard++ < 500) {
      const bas = sonuc[0], son = sonuc[sonuc.length - 1];
      // en yakın parça + birleşme tipini bul (son↔p0, son↔pSon, bas↔pSon, bas↔p0)
      let en = -1, enTip = 0, enD = Infinity;
      for (let i = 0; i < parcalar.length; i++) {
        const p = parcalar[i], p0 = p[0], pS = p[p.length - 1];
        const c = [d2(son, p0), d2(son, pS), d2(bas, pS), d2(bas, p0)];
        for (let t = 0; t < 4; t++) if (c[t] < enD) { enD = c[t]; en = i; enTip = t; }
      }
      const p = parcalar.splice(en, 1)[0];
      const oneKat = (arr: any[]) => { for (let k = arr.length - 1; k >= 0; k--) sonuc.unshift(arr[k]); };   // arr'ı sırasını koruyarak başa ekle
      if (enD > tol2) { for (const q of p) sonuc.push(q); continue; }   // kopuk → yine de ekle
      if (enTip === 0) p.slice(1).forEach(q => sonuc.push(q));          // son→p0: sona düz ekle
      else if (enTip === 1) p.slice(0, -1).reverse().forEach(q => sonuc.push(q));   // son→pSon: sona ters ekle
      else if (enTip === 2) oneKat(p.slice(0, -1));                     // bas→pSon: başa düz (p0..pLen-2)
      else oneKat(p.slice(1).reverse());                               // bas→p0: başa ters (pLen-1..p1)
    }
    return sonuc;
  }
}
